/**
 * Online checkout plumbing shared by online.chain29.com (apps/order) and the
 * chain29.com customer checkout (pages/CheckoutPage) - docs/CHECKOUT_API_2026-10-28.md §6.
 *
 * - One Idempotency-Key per checkout attempt (sessionStorage): a double click,
 *   a retry after a lost answer or a reload re-sends the SAME key, so the server
 *   answers the same order and page (never a second order). A changed cart, tip
 *   or "text me when ready" opt-in (`sms_notify`, default false) is a new
 *   attempt (a new key - the server answers 409 for a reused key with a
 *   different body).
 * - The placed order ({order_id, access_token, amounts, page}) is kept in
 *   sessionStorage and in the URL (?order=&t=), so a reload or the customer's
 *   return from the payment page resumes the poll.
 * - useOnlinePayment polls GET /online/orders/{id}/payment with backoff, bounded
 *   at ~5 min; returning from the page proves nothing - only the poll does.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '@/config/api';

const ATTEMPT_KEY = 'c29_online_checkout_attempt';
const ORDER_KEY = 'c29_online_checkout_order';
const BOUND_MS = 5 * 60 * 1000;

const read = (k) => { try { return JSON.parse(sessionStorage.getItem(k) || 'null'); } catch { return null; } };
const write = (k, v) => {
  try { if (v == null) sessionStorage.removeItem(k); else sessionStorage.setItem(k, JSON.stringify(v)); } catch { /* private mode */ }
};

function uuid() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function hashOf(text) {
  let h = 5381;
  for (let i = 0; i < text.length; i += 1) h = ((h << 5) + h + text.charCodeAt(i)) | 0;
  return String(h >>> 0);
}

/** The Idempotency-Key for this exact checkout body (same body -> same key). */
export function attemptKeyFor(slug, body) {
  const fingerprint = hashOf(JSON.stringify({ slug, body }));
  const saved = read(ATTEMPT_KEY);
  if (saved?.fingerprint === fingerprint && saved.key) return saved.key;
  const key = uuid();
  write(ATTEMPT_KEY, { key, fingerprint });
  return key;
}

export const forgetAttempt = () => write(ATTEMPT_KEY, null);

/** POST /online/{slug}/orders with the attempt's Idempotency-Key (+ the customer's token if signed in). */
export async function placeOnlineOrder(slug, body, { customerToken } = {}) {
  const headers = { 'Idempotency-Key': attemptKeyFor(slug, body) };
  if (customerToken) headers.Authorization = `Bearer ${customerToken}`;
  const res = await axios.post(`${API}/online/${slug}/orders`, body, { headers });
  rememberOrder(res.data);
  return res.data;
}

/** Keep what the order answer said (amounts + the open page) for a reload / return. */
export function rememberOrder(answer, extra = {}) {
  if (!answer?.order_id) return;
  const prev = read(ORDER_KEY);
  const same = prev?.order_id === answer.order_id;
  write(ORDER_KEY, {
    ...(same ? prev : {}),
    order_id: answer.order_id,
    access_token: answer.access_token || (same ? prev.access_token : undefined),
    subtotal_cents: answer.subtotal_cents ?? (same ? prev.subtotal_cents : undefined),
    discount_cents: answer.discount_cents ?? (same ? prev.discount_cents : undefined),
    tax_cents: answer.tax_cents ?? (same ? prev.tax_cents : undefined),
    tip_cents: answer.tip_cents ?? (same ? prev.tip_cents : undefined),
    total_cents: answer.total_cents ?? (same ? prev.total_cents : undefined),
    payment: answer.payment || (same ? prev.payment : null),
    ...extra,
  });
}

export function rememberedOrder(orderId) {
  const saved = read(ORDER_KEY);
  if (!saved) return null;
  return orderId == null || String(saved.order_id) === String(orderId) ? saved : null;
}

export function forgetOrder() {
  write(ORDER_KEY, null);
  forgetAttempt();
}

/** POST pay-again: paid late -> {payment_status: 'paid'}; else the open (or a new) page. */
export async function payAgain(orderId, accessToken) {
  const res = await axios.post(`${API}/online/orders/${orderId}/pay-again`, {}, { params: { t: accessToken } });
  if (res.data?.payment) rememberOrder({ order_id: orderId, access_token: accessToken, payment: res.data.payment });
  return res.data;
}

/** The QR to show: the Hub's own image verbatim when it sent one, else none (render pay_url). */
export function hubQrImage(page) {
  const img = page?.qr_image;
  if (!img) return null;
  if (/^(data:|https?:)/.test(img)) return img;
  return `data:image/png;base64,${img}`;
}

// 2 s for the first 30 s, then 3 s, 5 s, 10 s - bounded at ~5 min.
function nextDelay(elapsed) {
  if (elapsed < 30000) return 2000;
  if (elapsed < 90000) return 3000;
  if (elapsed < 180000) return 5000;
  return 10000;
}

/**
 * Poll the order's payment. state:
 *   loading | waiting (page open) | paid | declined | cancelled | timed_out | error |
 *   order_cancelled | not_found
 * `confirming` turns true after ~5 min without a final answer (polling stops;
 * `checkAgain()` asks once more and restarts the bounded wait).
 */
export function useOnlinePayment(orderId, accessToken, { onPaid } = {}) {
  const [data, setData] = useState(null);
  const [state, setState] = useState(orderId ? 'loading' : 'idle');
  const [confirming, setConfirming] = useState(false);
  const [netError, setNetError] = useState(false);
  const startedAt = useRef(Date.now());
  const timer = useRef(null);
  const stopped = useRef(false);
  const onPaidRef = useRef(onPaid);
  onPaidRef.current = onPaid;

  const poll = useCallback(async () => {
    clearTimeout(timer.current);
    if (!orderId || !accessToken) return;
    let final = false;
    try {
      const res = await axios.get(`${API}/online/orders/${orderId}/payment`, { params: { t: accessToken } });
      const d = res.data;
      setData(d);
      setNetError(false);
      if (d.payment_status === 'paid') {
        final = true;
        setState('paid');
        onPaidRef.current?.(d);
      } else if (d.order_status === 'cancelled' && !d.can_pay_again) {
        final = true;
        setState('order_cancelled');
      } else if (d.is_final) {
        final = true;
        setState(['declined', 'cancelled', 'timed_out', 'error'].includes(d.status) ? d.status : 'error');
      } else {
        setState('waiting');
      }
    } catch (e) {
      if (e?.response?.status === 404) {
        final = true;
        setState('not_found');
      } else {
        setNetError(true);
        setState((s) => (s === 'loading' ? 'waiting' : s));
      }
    }
    if (final || stopped.current) return;
    const elapsed = Date.now() - startedAt.current;
    if (elapsed >= BOUND_MS) { setConfirming(true); return; }
    timer.current = setTimeout(poll, nextDelay(elapsed));
  }, [orderId, accessToken]);

  const restart = useCallback(() => {
    startedAt.current = Date.now();
    setConfirming(false);
    poll();
  }, [poll]);

  useEffect(() => {
    stopped.current = false;
    if (!orderId) return undefined;
    restart();
    const onVisible = () => { if (document.visibilityState === 'visible') poll(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', poll);
    return () => {
      stopped.current = true;
      clearTimeout(timer.current);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', poll);
    };
  }, [orderId, poll, restart]);

  return { state, data, confirming, netError, checkAgain: restart, setState };
}
